import { Box, Button } from "@mui/material";
import React, { ReactElement, ReactNode } from "react";

import {
  IAction,
  IField,
  IPageable,
  Params,
} from "../../types/table/interfaces.type";
import SearchableTable from "./SearchableTable";

interface CustomTableProps<T> {
  filters: Params<T>;
  fields: IField<T>[];
  fetchFunc:
    | ((params: Params<T>) => Promise<IPageable<T>>)
    | ((id: string) => Promise<T>);
  actions?: IAction<T>[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  shadeColor?: string;
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  /** Clicking anywhere on a row (not on its buttons) opens it. */
  onRowClick?: (row: T) => void;
  sortableFields?: (keyof T)[];
  highlightDiff?: boolean;
  clientSidePagination?: boolean;
  refreshSignal?: any;
  loading?: boolean;
  paginationSummary?: ReactNode;
}

function CustomTable<T>({
  filters,
  fields,
  fetchFunc,
  actions = [],
  shadeRow,
  shadeBg,
  shadeColor,
  checkboxSelect,
  isRowSelected,
  onRowClick,
  onRowSelect,
  sortableFields,
  highlightDiff = false,
  clientSidePagination = false,
  refreshSignal,
  loading,
  paginationSummary,
}: CustomTableProps<T>) {
  const renderActions = (row: T): ReactElement[] => {
  const visibleActions = actions.filter((action) => {
  if (typeof action.visible === "function") {
    return action.visible(row);
  }
  return action.visible !== false;
});
  return visibleActions
    .map((action) => {
      if (action.component) {
        const customComponent = action.component(row);
        if (React.isValidElement(customComponent)) {
          return customComponent;
        }
        return null;
      }

      return (
        <Button
          key={action.id}
          disabled={isDisabled<T>(action, row)}
          onClick={() => action.handler(row)}
          onContextMenu={
            action.onContextMenu
              ? (event: React.MouseEvent) => {
                  event.preventDefault();
                  action.onContextMenu!(event, row);
                }
              : undefined
          }
          sx={{
            margin: "0 3px",
            width: "min-content",
            height: "min-content",
            minWidth: "0",
            padding: 0.25,
          }}
        >
          {typeof action.icon === "function" ? action.icon(row) : action.icon}
        </Button>
      );
    })
    .filter((element): element is ReactElement =>
      React.isValidElement(element)
    );
};

  return (
    <Box sx={{ width: "100%", borderRadius: "10px" }}>
      <SearchableTable<T>
        filters={filters}
        fields={fields}
        fetchFunc={fetchFunc}
        actions={actions}
        renderActions={renderActions}
        shadeRow={shadeRow}
        shadeBg={shadeBg}
        shadeColor={shadeColor}
        checkboxSelect={checkboxSelect}
        onRowSelect={onRowSelect}
        isRowSelected={isRowSelected}
        onRowClick={onRowClick}
        sortableFields={sortableFields}
        highlightDiff={highlightDiff}
        clientSidePagination={clientSidePagination}
        refreshSignal={refreshSignal}
        loading={loading}
        paginationSummary={paginationSummary}
      />
    </Box>
  );
}

export default CustomTable;
function isDisabled<T>(action: IAction<T>, row: T): boolean {
  if (typeof action.disabled == "function") {
    return action.disabled(row);
  }
  return action.disabled === true;
}
